import React, { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { UnsavedChangesGuard } from '@shared';
import Sidebar from './Sidebar';
import AlarmDetailModal from '@/components/AlarmDetailModal';
import EventDetailModal from '@/components/EventDetailModal';
import CommandPalette from '@/components/CommandPalette';
import { Icon } from '@shared';

// Sólo el kanban de Desarrollo necesita el ancho entero. /planes lo tuvo
// mientras Límites eran nueve tablas apiladas; desde el 2026-10-25 sus tablas
// miden lo que su contenido y la página va en la columna centrada como todas.
const WIDE_ROUTES = ['/desarrollo'];

const MainLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const location = useLocation();
  const isWide = WIDE_ROUTES.some((r) => location.pathname.startsWith(r));

  // Ctrl/Cmd+K abre la búsqueda global. No dispara con el foco en un campo de
  // texto ni con otro diálogo abierto, para no robar el atajo a un editor.
  const onKey = useCallback((e: KeyboardEvent) => {
    if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'k' || e.altKey || e.shiftKey) return;
    const t = e.target as HTMLElement | null;
    const tag = t?.tagName;
    if (t && (tag === 'INPUT' || tag === 'TEXTAREA' || t.isContentEditable)) return;
    if (!paletteOpen && document.querySelector('[role="dialog"]')) return;
    e.preventDefault();
    setPaletteOpen((o) => !o);
  }, [paletteOpen]);
  useEffect(() => {
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onKey]);

  return (
    <div className="flex h-screen w-full overflow-hidden bg-background-light dark:bg-background-dark">
      <div className="hidden md:flex">
        <Sidebar />
      </div>

      {/* Mobile overlay */}
      <div
        className={`fixed inset-0 z-30 transition-opacity duration-300 md:hidden ${
          sidebarOpen ? 'bg-black/60' : 'bg-transparent pointer-events-none'
        }`}
        onClick={() => setSidebarOpen(false)}
      />
      <div
        className={`fixed top-0 left-0 h-full z-40 transition-transform duration-300 md:hidden ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <Sidebar onClose={() => setSidebarOpen(false)} />
      </div>

      {/* Mobile menu trigger — floating */}
      <button
        type="button"
        onClick={() => setSidebarOpen(true)}
        aria-label="Abrir menú"
        className="md:hidden fixed top-3 left-3 z-20 p-2 rounded-lg bg-white dark:bg-surface-dark border border-border-subtle dark:border-border-subtle-dark text-text-main dark:text-gray-300 shadow-sm focus:outline-none"
      >
        <Icon name="menu" />
      </button>

      <main className="flex-1 overflow-x-hidden overflow-y-auto">
        <div className={`p-4 pt-16 md:pt-8 md:p-8 mx-auto ${isWide ? 'max-w-full' : 'max-w-7xl'}`}>
          <Outlet />
        </div>
      </main>

      {/* Los dos detalles viven aquí, una vez: se abren desde cualquier
          pantalla con openAlarmDetail / openEventDetail. */}
      <AlarmDetailModal />
      <EventDetailModal />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <UnsavedChangesGuard />
    </div>
  );
};

export default MainLayout;
